import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Toast } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle, faInfoCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

const ToastContext = createContext({ showToast: () => {}, hideToast: () => {} });

const TOAST_VARIANTS = {
  success: {
    accentClassName: "border-success",
    icon: faCheckCircle,
    iconClassName: "text-success",
    title: "Success"
  },
  danger: {
    accentClassName: "border-danger",
    icon: faTimesCircle,
    iconClassName: "text-danger",
    title: "Something went wrong"
  },
  warning: {
    accentClassName: "border-warning",
    icon: faExclamationTriangle,
    iconClassName: "text-warning",
    title: "Heads up"
  },
  info: {
    accentClassName: "border-info",
    icon: faInfoCircle,
    iconClassName: "text-info",
    title: "Notice"
  }
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const hideToast = useCallback((id) => {
    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(({ message, title, variant = "info", delay = 3500 }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    setToasts((currentToasts) => [
      ...currentToasts,
      {
        id,
        message,
        title,
        variant,
        delay
      }
    ]);
  }, []);

  const contextValue = useMemo(() => ({ showToast, hideToast }), [hideToast, showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div
        className="position-fixed p-3"
        style={{ pointerEvents: "auto", top: "1rem", right: "1rem", zIndex: 1080, width: "min(380px, calc(100vw - 2rem))" }}
      >
        {toasts.map((toast) => {
          const config = TOAST_VARIANTS[toast.variant] || TOAST_VARIANTS.info;

          return (
            <Toast
              key={toast.id}
              show
              onClose={() => hideToast(toast.id)}
              delay={toast.delay}
              autohide
              className={`mb-3 shadow-lg border-start border-4 ${config.accentClassName}`}
              bg="white"
            >
              <Toast.Header className="bg-white">
                <FontAwesomeIcon icon={config.icon} className={`${config.iconClassName} me-2`} />
                <strong className="me-auto">{toast.title || config.title}</strong>
                <small className="text-muted">now</small>
              </Toast.Header>
              <Toast.Body className="text-dark">{toast.message}</Toast.Body>
            </Toast>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
